// src/pages/TTSDiagnosticsPage.jsx
// P0 PRODUCTION DIAGNOSTICS: Browser-only verification screen
// Calls /api/creator/diagnostics/* endpoints and renders raw JSON
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PublisherNav from '../components/PublisherNav';
import UniversalFooter from '../components/UniversalFooter';
import TTSProofReportModal from '../components/TTSProofReportModal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Activity, RefreshCw, Play, Eye, Copy, Check, AlertCircle, 
  CheckCircle, Clock, Server, Database, Loader2, ArrowLeft, FileText
} from 'lucide-react';
import { BACKEND_URL } from '@/config/api';

const TTSDiagnosticsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState({});
  const [results, setResults] = useState({});
  const [showProofReport, setShowProofReport] = useState(false);
  const [copied, setCopied] = useState({});
  const [titleIdInput, setTitleIdInput] = useState('');

  const callEndpoint = async (key, endpoint, method = 'GET', body = null) => {
    setLoading(prev => ({ ...prev, [key]: true }));
    
    try {
      const token = localStorage.getItem('accessToken');
      const options = {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      };
      
      if (body) {
        options.body = JSON.stringify(body);
      }
      
      const startTime = Date.now();
      const response = await fetch(`${BACKEND_URL}${endpoint}`, options);
      const latency = Date.now() - startTime;
      
      let data;
      try {
        data = await response.json();
      } catch {
        data = { error: 'Non-JSON response', status: response.status };
      }
      
      setResults(prev => ({
        ...prev,
        [key]: {
          status: response.status,
          ok: response.ok,
          latency,
          timestamp: new Date().toISOString(),
          data
        }
      }));
    } catch (error) {
      setResults(prev => ({
        ...prev,
        [key]: {
          status: 'ERROR',
          ok: false,
          error: error.message,
          timestamp: new Date().toISOString()
        }
      }));
    } finally {
      setLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  const copyToClipboard = async (key) => {
    const result = results[key];
    if (result) {
      await navigator.clipboard.writeText(JSON.stringify(result, null, 2));
      setCopied(prev => ({ ...prev, [key]: true }));
      setTimeout(() => setCopied(prev => ({ ...prev, [key]: false })), 2000);
    }
  };

  const renderResult = (key, title) => {
    const result = results[key];
    const isLoading = loading[key];
    
    return (
      <div className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700">
          <h3 className="font-medium text-slate-200">{title}</h3>
          <div className="flex items-center gap-2">
            {result && (
              <>
                <span className={`text-xs px-2 py-0.5 rounded ${
                  result.ok ? 'bg-emerald-900/50 text-emerald-400' : 'bg-red-900/50 text-red-400'
                }`}>
                  {result.status}
                </span>
                {result.latency && (
                  <span className="text-xs text-slate-500">{result.latency}ms</span>
                )}
                <button
                  onClick={() => copyToClipboard(key)}
                  className="p-1 hover:bg-slate-700 rounded"
                  title="Copy JSON"
                >
                  {copied[key] ? (
                    <Check className="w-4 h-4 text-emerald-400" />
                  ) : (
                    <Copy className="w-4 h-4 text-slate-400" />
                  )}
                </button>
              </>
            )}
          </div>
        </div>
        <div className="p-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-indigo-400" />
            </div>
          ) : result ? (
            <pre className="text-xs text-slate-300 overflow-x-auto whitespace-pre-wrap max-h-96 overflow-y-auto font-mono bg-slate-900/50 rounded p-3">
              {JSON.stringify(result.data, null, 2)}
            </pre>
          ) : (
            <p className="text-slate-500 text-sm text-center py-4">Click button to fetch</p>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50">
      <PublisherNav />
      
      <main className="mx-auto max-w-6xl px-4 py-8">
        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-slate-400 hover:text-slate-200 mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="rounded-full bg-purple-600/20 p-2">
              <Activity className="w-6 h-6 text-purple-400" />
            </div>
            <h1 className="text-2xl font-bold">TTS Diagnostics</h1>
          </div>
          <p className="text-slate-400">
            Production verification tools. Copy responses from DevTools Network → Response for proof artifacts.
          </p>
        </div>

        {/* System Status Section */}
        <section className="mb-8">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Server className="w-5 h-5 text-indigo-400" />
            System Status
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <Button
              onClick={() => callEndpoint('executor', '/api/creator/diagnostics/executor-status')}
              disabled={loading.executor}
              className="bg-indigo-600 hover:bg-indigo-500"
              data-testid="btn-executor-status"
            >
              {loading.executor ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Activity className="w-4 h-4 mr-2" />}
              Get Executor Status
            </Button>
            
            <Button
              onClick={() => callEndpoint('queue', '/api/creator/diagnostics/queue-snapshot')}
              disabled={loading.queue}
              className="bg-indigo-600 hover:bg-indigo-500"
              data-testid="btn-queue-snapshot"
            >
              {loading.queue ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Database className="w-4 h-4 mr-2" />}
              Get Queue Snapshot
            </Button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {renderResult('executor', 'Executor Status')}
            {renderResult('queue', 'Queue Snapshot')}
          </div>
        </section>

        {/* TTS Start Section */}
        <section className="mb-8">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Play className="w-5 h-5 text-emerald-400" />
            TTS Start (Hard Trigger)
          </h2>
          
          <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4 mb-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <Label htmlFor="titleId" className="text-sm text-slate-400 mb-1 block">Title ID</Label>
                <Input
                  id="titleId"
                  value={titleIdInput}
                  onChange={(e) => setTitleIdInput(e.target.value)}
                  placeholder="Enter title ID (e.g., abc123-def456...)"
                  className="bg-slate-900 border-slate-600"
                  data-testid="input-title-id"
                />
              </div>
              <div className="flex items-end">
                <Button
                  onClick={() => {
                    if (titleIdInput.trim()) {
                      callEndpoint(
                        'ttsStart',
                        `/api/publisher/titles/${titleIdInput.trim()}/tts/start`,
                        'POST',
                        { force_reset: true }
                      );
                    }
                  }}
                  disabled={loading.ttsStart || !titleIdInput.trim()}
                  className="bg-emerald-600 hover:bg-emerald-500"
                  data-testid="btn-tts-start"
                >
                  {loading.ttsStart ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
                  Call /tts/start
                </Button>
                <Button
                  onClick={() => {
                    if (titleIdInput.trim()) {
                      setShowProofReport(true);
                    }
                  }}
                  disabled={!titleIdInput.trim()}
                  className="bg-indigo-600 hover:bg-indigo-500"
                  data-testid="btn-proof-report"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Proof Report
                </Button>
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-2">
              Response MUST show <code className="bg-slate-700 px-1 rounded">state: &quot;QUEUED&quot;</code> and <code className="bg-slate-700 px-1 rounded">kick_requested_at</code> timestamp.
              If it shows <code className="bg-slate-700 px-1 rounded">state: &quot;DONE&quot;</code>, the contract is violated.
            </p>
          </div>
          
          {renderResult('ttsStart', '/tts/start Response')}
        </section>
        
        {/* Proof Report Modal */}
        <TTSProofReportModal
          open={showProofReport}
          onOpenChange={setShowProofReport}
          titleId={titleIdInput.trim()}
        />

        {/* Verification Checklist */}
        <section className="mb-8">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-amber-400" />
            Production Verification Checklist
          </h2>
          
          <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4">
            <ol className="space-y-3 text-sm">
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-600 text-white flex items-center justify-center text-xs font-bold">1</span>
                <div>
                  <p className="text-slate-200 font-medium">Call /tts/start</p>
                  <p className="text-slate-400 text-xs">Response must include: <code className="bg-slate-700 px-1 rounded">job_id</code>, <code className="bg-slate-700 px-1 rounded">kick_requested_at</code>, <code className="bg-slate-700 px-1 rounded">state=QUEUED</code></p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-600 text-white flex items-center justify-center text-xs font-bold">2</span>
                <div>
                  <p className="text-slate-200 font-medium">Get Queue Snapshot (within 5 seconds)</p>
                  <p className="text-slate-400 text-xs">Same <code className="bg-slate-700 px-1 rounded">job_id</code> shows <code className="bg-slate-700 px-1 rounded">state=EXECUTING</code>, <code className="bg-slate-700 px-1 rounded">kick_requested_at</code> is absent</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-600 text-white flex items-center justify-center text-xs font-bold">3</span>
                <div>
                  <p className="text-slate-200 font-medium">Check Worker Logs</p>
                  <p className="text-slate-400 text-xs">Must contain: <code className="bg-slate-700 px-1 rounded">[CLAIM] ✓ ATOMIC CLAIM SUCCESS</code></p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-600 text-white flex items-center justify-center text-xs font-bold">4</span>
                <div>
                  <p className="text-slate-200 font-medium">Verify R2 Keys</p>
                  <p className="text-slate-400 text-xs">Segment mp3 files + manifest exist in storage</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-600 text-white flex items-center justify-center text-xs font-bold">5</span>
                <div>
                  <p className="text-slate-200 font-medium">Pod Restart Playback</p>
                  <p className="text-slate-400 text-xs">Restart pod, reload player, audio still plays</p>
                </div>
              </li>
            </ol>
          </div>
        </section>

        {/* Quick Actions */}
        <section>
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <RefreshCw className="w-5 h-5 text-cyan-400" />
            Quick Actions
          </h2>
          
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => {
                callEndpoint('executor', '/api/creator/diagnostics/executor-status');
                callEndpoint('queue', '/api/creator/diagnostics/queue-snapshot');
              }}
              disabled={loading.executor || loading.queue}
              className="border-slate-600"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh All
            </Button>
            
            <Button
              variant="outline"
              onClick={() => callEndpoint('health', '/api/health')}
              disabled={loading.health}
              className="border-slate-600"
            >
              <Activity className="w-4 h-4 mr-2" />
              Health Check
            </Button>
          </div>
          
          {results.health && (
            <div className="mt-4">
              {renderResult('health', 'Health Check')}
            </div>
          )}
        </section>
      </main>
      
      <UniversalFooter />
    </div>
  );
};

export default TTSDiagnosticsPage;
